import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as eventService from "../services/eventService";

function etiquetaEstado(estado: string) {
  return normalizarEstadoEvento(estado).toUpperCase();
}

function normalizarEstadoEvento(estado: string) {
  if (estado === "BORRADOR" || estado === "CONFIRMADO") return "PLANIFICADO";
  if (estado === "COMPLETADO") return "TERMINADO";
  return estado || "PLANIFICADO";
}

function claseBadgeEstado(estado: string) {
  const normalized = normalizarEstadoEvento(estado);
  if (normalized === "ENVIADO")
    return "bg-emerald-500/20 text-emerald-400 border border-emerald-500/20";
  if (normalized === "PLANIFICADO")
    return "bg-purple-500/20 text-purple-400 border border-purple-500/20";
  if (normalized === "TERMINADO")
    return "bg-blue-500/20 text-blue-400 border border-blue-500/20";
  if (normalized === "CANCELADO")
    return "bg-red-500/20 text-red-400 border border-red-500/20";
  return "bg-amber-500/20 text-amber-400 border border-amber-500/20";
}

function formatoFecha(iso: string) {
  try {
    return new Intl.DateTimeFormat("es-PE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const EVENT_CARD_IMAGE =
  "https://images.unsplash.com/photo-1529634806980-85c3dd6d34ac?auto=format&fit=crop&w=1400&q=80";

function RsvpPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<eventService.EventoAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("TODOS");

  async function cargar() {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await eventService.listarEventos();
      setEvents(data);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error cargando eventos");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return events.filter((ev) => {
      const matchQuery =
        !query ||
        ev.nombre.toLowerCase().includes(query) ||
        ev.slug.toLowerCase().includes(query);

      const matchStatus = status === "TODOS" ? true : normalizarEstadoEvento(ev.estado) === status;
      return matchQuery && matchStatus;
    });
  }, [events, q, status]);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto w-full flex flex-col gap-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 dark:text-white mb-2">
            RSVP
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            Consulta el resumen de confirmaciones por evento.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-white dark:bg-surface-dark p-4 rounded-xl border border-slate-200 dark:border-white/5 shadow-sm">
        <div className="md:col-span-6 relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
            search
          </span>
          <input
            className="block w-full pl-10 pr-3 py-3 border border-slate-200 dark:border-white/10 rounded-lg leading-5 bg-slate-50 dark:bg-background-dark text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm transition-all shadow-sm"
            placeholder="Buscar por nombre o identificador (slug)..."
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="md:col-span-4 relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
            filter_list
          </span>
          <select
            className="block w-full pl-10 pr-10 py-3 border border-slate-200 dark:border-white/10 rounded-lg leading-5 bg-slate-50 dark:bg-background-dark text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm appearance-none cursor-pointer"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="TODOS">Todos los estados</option>
            <option value="PLANIFICADO">Planificado</option>
            <option value="ENVIADO">Enviado</option>
            <option value="TERMINADO">Terminado</option>
            <option value="CANCELADO">Cancelado</option>
          </select>
          <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
            expand_more
          </span>
        </div>

        <div className="md:col-span-2 flex justify-end">
          <button
            type="button"
            onClick={cargar}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors dark:bg-background-dark dark:hover:bg-white/5"
          >
            <span className="material-symbols-outlined text-[18px]">refresh</span>
            Actualizar
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-slate-500 dark:text-slate-400">
          Cargando eventos...
        </div>
      )}
      {!loading && errorMsg && <div className="text-red-500">{errorMsg}</div>}

      {!loading && !errorMsg && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((ev) => (
            <div
              key={ev.id}
              className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/5 rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300 group flex flex-col shadow-sm"
            >
              <div className="h-44 relative overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.03]"
                  style={{
                    backgroundImage: `linear-gradient(to top, rgba(25,17,33,0.95), rgba(25,17,33,0.35)), url('${EVENT_CARD_IMAGE}')`,
                  }}
                />

                <div className="absolute top-4 left-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium backdrop-blur-md ${claseBadgeEstado(
                      ev.estado
                    )}`}
                  >
                    {etiquetaEstado(ev.estado)}
                  </span>
                </div>
              </div>

              <div className="p-6 flex flex-col flex-1 gap-5 -mt-10 relative z-10">
                <div className="flex flex-col gap-2">
                  <h3 className="text-2xl font-serif font-bold text-slate-900 dark:text-white leading-tight">
                    {ev.nombre}
                  </h3>

                  <div className="flex flex-col gap-2 mt-1">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm">
                      <span className="material-symbols-outlined text-primary text-[18px]">
                        calendar_today
                      </span>
                      <span>{formatoFecha(ev.fechaPrincipal)}</span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm">
                      <span className="material-symbols-outlined text-primary text-[18px]">
                        event_available
                      </span>
                      <span>RSVP hasta {ev.fechaLimiteRsvp ? formatoFecha(ev.fechaLimiteRsvp) : "sin fecha"}</span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-300 text-sm">
                      <span className="material-symbols-outlined text-primary text-[18px]">
                        tag
                      </span>
                      <span className="truncate">{ev.slug}</span>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-slate-200 dark:bg-white/10 w-full"></div>

                <div className="mt-auto pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    className="w-full h-11 rounded-lg bg-primary/90 hover:bg-primary text-white font-semibold transition-all shadow-lg shadow-primary/20 active:scale-[0.99]"
                    type="button"
                    onClick={() => navigate(`/home/rsvp/eventos/${ev.id}/resumen`)}
                  >
                    Ver Resumen
                  </button>
                  <button
                    className="w-full h-11 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white font-semibold transition-all active:scale-[0.99]"
                    type="button"
                    onClick={() => navigate(`/home/rsvp/eventos/${ev.id}/invitaciones-enviadas`)}
                  >
                    Invitaciones Enviadas
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RsvpPage;
